import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUp, ArrowRight } from 'lucide-react';
import { useT } from '../i18n';
import { trackEvent } from '../lib/analytics';
import { getRef } from '../lib/referral';

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = 'kx_visitor_path';
const SERIF = { fontFamily: "'Playfair Display', Georgia, serif" };

const CATS = [
  { id: 'reviews', re: /rese|review|reputaci|estrella|star|rating|google maps|opinione/i },
  { id: 'marketing', re: /marketing|redes|social|instagram|tiktok|facebook|clientes nuevos|visibilidad|promo|más clientes|mas clientes|more customers|guests|llenar/i },
  { id: 'delivery', re: /delivery|uber|glovo|rappi|doordash|didi|just eat|pedido|takeaway|para llevar|comisi/i },
  { id: 'costs', re: /cost[oe]|inventar|inventory|merma|waste|margen|margin|profit|ganancia|gasto|food cost|números|numeros|numbers/i },
  { id: 'staff', re: /personal|staff|equipo|turno|schedule|emplead|rotaci|team|contratar|hiring/i },
  { id: 'opening', re: /abrir|abriendo|montar|opening|nuevo restaurante|new restaurant|empezar|start(ing)? a|voy a abrir/i },
  { id: 'pricing', re: /precio|price|cu[aá]nto (cuesta|vale)|how much|tarifa|planes|plans/i },
  { id: 'website', re: /\bweb\b|p[aá]gina|website|men[uú] digital|\bqr\b|carta digital/i },
  { id: 'demo', re: /demo|probar|try it|show me|ver c[oó]mo|enséñame|ensename/i },
];
const detectCategory = (text) => (CATS.find((c) => c.re.test(text)) || { id: 'other' }).id;

const REPLIES = {
  reviews: {
    es: 'Las reseñas pesan más de lo que parece. Nuestra IA vigila Google, responde con tu tono y te avisa antes de que una mala reseña haga daño.',
    en: 'Reviews weigh more than they seem. Our AI watches Google, replies in your tone and warns you before a bad review does damage.',
  },
  marketing: {
    es: 'Más clientes es el objetivo de todo lo que hacemos: contenido, campañas y presencia digital generados por IA, sin agencias de por medio.',
    en: 'More customers is the goal behind everything we do: AI-generated content, campaigns and digital presence — no agencies in between.',
  },
  delivery: {
    es: 'Las comisiones del delivery se comen el margen en silencio. Analizamos tus plataformas y te decimos exactamente dónde lo estás perdiendo.',
    en: 'Delivery fees eat your margin quietly. We analyze your platforms and tell you exactly where you are losing it.',
  },
  costs: {
    es: 'Food cost, mermas y márgenes por plato, claros cada mañana. Es donde más rápido se nota Kitxens.',
    en: 'Food cost, waste and per-dish margins, clear every morning. It is where Kitxens shows fastest.',
  },
  staff: {
    es: 'La IA te quita de encima el trabajo operativo para que tú puedas dedicarte a liderar al equipo.',
    en: 'The AI takes the operational load off your plate so you can focus on leading the team.',
  },
  opening: {
    es: 'Abrir un restaurante es el mejor momento para empezar bien: marca, web, menú y números desde el día cero.',
    en: 'Opening a restaurant is the best moment to start right: brand, website, menu and numbers from day zero.',
  },
  pricing: {
    es: 'Planes claros y sin sorpresas. Y la demo es gratis, para que veas el valor antes de pagar nada.',
    en: 'Clear plans, no surprises. And the demo is free, so you see the value before paying anything.',
  },
  website: {
    es: 'Tu web y tu carta digital son tu primera impresión. Las creamos y las mantenemos con IA.',
    en: 'Your website and digital menu are your first impression. We create and maintain them with AI.',
  },
  demo: {
    es: 'La demo usa datos reales de ejemplo de un restaurante de verdad. En dos minutos ves lo que la IA haría por el tuyo.',
    en: 'The demo runs on real sample data from an actual restaurant. In two minutes you see what the AI would do for yours.',
  },
  other: {
    es: 'Kitxens es un equipo de IAs que lleva el marketing, las reseñas, el delivery y los números de tu restaurante.',
    en: 'Kitxens is a team of AIs running your restaurant\u2019s marketing, reviews, delivery and numbers.',
  },
};

const CHIPS = [
  ['marketing', 'Quiero más clientes', 'I want more customers'],
  ['pricing', '¿Cuánto cuesta?', 'How much is it?'],
  ['demo', 'Enséñame la demo', 'Show me the demo'],
];

const PLACEHOLDERS = {
  es: ['Quiero llenar el local entre semana…', 'Las reseñas de Google me preocupan…', 'El delivery se come mi margen…', 'Voy a abrir un restaurante…'],
  en: ['I want to fill the place on weekdays…', 'Google reviews worry me…', 'Delivery is eating my margin…', 'I\u2019m opening a restaurant…'],
};

export const WelcomeGate = () => {
  const { lang } = useT();
  const isEs = lang === 'es';
  const L = isEs ? 'es' : 'en';
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [ph, setPh] = useState('');
  const [msgs, setMsgs] = useState([]);
  const [step, setStep] = useState('pain');
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [cat, setCat] = useState('other');
  const [contact, setContact] = useState({ name: '', restaurant: '', email: '' });
  const dataRef = useRef({ message: '', category: 'other' });
  const bottomRef = useRef(null);

  const greeting = isEs
    ? 'Hola, soy Rachel.\n¿En qué podemos ayudarte?'
    : 'Hi, I\u2019m Rachel.\nHow can we help you?';

  useEffect(() => {
    if (!localStorage.getItem(KEY)) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setTyped('');
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setTyped(greeting.slice(0, i));
      if (i >= greeting.length) clearInterval(t);
    }, 24);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Emergent-style self-typing placeholder examples
  useEffect(() => {
    if (!open || step !== 'pain') return undefined;
    const list = PLACEHOLDERS[L];
    let pi = 0, ci = 0, hold = 0;
    const t = setInterval(() => {
      const cur = list[pi % list.length];
      if (ci < cur.length) { ci += 1; setPh(cur.slice(0, ci)); }
      else if (hold < 34) { hold += 1; }
      else { pi += 1; ci = 0; hold = 0; setPh(''); }
    }, 50);
    return () => clearInterval(t);
  }, [open, step, L]);

  useEffect(() => {
    const box = bottomRef.current?.parentElement;
    if (box) box.scrollTop = box.scrollHeight; // scroll chat pane only, never the page
  }, [msgs, thinking, step]);

  const rachelSays = (text, nextStep, delay = 850) => {
    setThinking(true);
    setTimeout(() => {
      setThinking(false);
      setMsgs((m) => [...m, { from: 'rachel', text }]);
      if (nextStep) setStep(nextStep);
    }, delay);
  };

  const sendPain = (text, forcedCat) => {
    const msg = text.trim();
    if (!msg) return;
    const c = forcedCat || detectCategory(msg);
    setCat(c);
    dataRef.current = { ...dataRef.current, message: msg, category: c };
    localStorage.setItem('kx_first_interest', c);
    trackEvent('welcome_pain_shared', { category: c });
    setMsgs((m) => [...m, { from: 'user', text: msg }]);
    setInput('');
    setStep('wait');
    rachelSays(REPLIES[c][L], null, 900);
    setTimeout(() => rachelSays(
      isEs ? 'Lo mejor es que lo veas funcionando: la demo son dos minutos, sin tarjeta. ¿Me dices tu nombre y tu restaurante y te la dejo preparada?'
        : 'The best part is seeing it work: the demo takes two minutes, no card. Tell me your name and your restaurant and I\u2019ll have it ready for you?',
      'contact', 700,
    ), 1300);
  };

  const saveIntent = (c) => {
    fetch(`${API}/api/welcome/intent`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...dataRef.current, name: c.name, restaurant: c.restaurant, email: c.email,
        locale: lang, ref_code: getRef() || '', market: localStorage.getItem('kx_market') || '' }),
    }).catch(() => {});
  };

  const submitContact = (skipped) => {
    const c = skipped ? { name: '', restaurant: '', email: '' }
      : { name: contact.name.trim(), restaurant: contact.restaurant.trim(), email: contact.email.trim() };
    if (!skipped && !/.+@.+\..+/.test(c.email)) return;
    if (c.name) localStorage.setItem('kx_welcome_name', c.name);
    if (c.email) localStorage.setItem('kx_welcome_email', c.email);
    if (c.restaurant) localStorage.setItem('kx_welcome_restaurant', c.restaurant);
    trackEvent('welcome_contact', { provided: !skipped });
    setMsgs((m) => [...m, { from: 'user', text: skipped ? (isEs ? 'Prefiero mirar con calma' : 'I\u2019d rather browse calmly') : `${c.name} · ${c.restaurant}` }]);
    saveIntent(c);
    setStep('wait');
    const first = c.name ? c.name.split(' ')[0] : '';
    const rest = c.restaurant || '';
    rachelSays(
      isEs ? `${first ? `Encantada, ${first}. ` : 'Perfecto. '}Tu demo está lista — dos minutos y ves exactamente lo que la IA haría por ${rest || 'tu restaurante'}:`
        : `${first ? `Nice to meet you, ${first}. ` : 'Perfect. '}Your demo is ready — two minutes and you\u2019ll see exactly what the AI would do for ${rest || 'your restaurant'}:`,
      'route', 750,
    );
  };

  const go = (path, label) => {
    localStorage.setItem(KEY, 'chat');
    trackEvent('welcome_route_chosen', { route: label, category: cat });
    setOpen(false);
    if (path) nav(path);
  };

  const skip = () => {
    localStorage.setItem(KEY, 'skip');
    trackEvent('visitor_path_chosen', { visitor_path: 'skip' });
    if (dataRef.current.message) saveIntent({ name: contact.name.trim(), restaurant: contact.restaurant.trim(), email: contact.email.trim() });
    setOpen(false);
  };

  const routes = [
    { to: '/demo', primary: true, label: isEs ? 'Ver mi demo ahora · 2 min' : 'See my demo now · 2 min', id: 'demo' },
    ...(cat === 'pricing' ? [{ to: '/pricing', label: isEs ? 'Precios y planes' : 'Pricing & plans', id: 'pricing' }] : []),
    { to: '/magazine', label: isEs ? 'Casos y guías' : 'Cases & guides', id: 'magazine' },
    { to: null, label: isEs ? 'Prefiero mirar la web con calma' : 'I prefer browsing the site calmly', id: 'browse' },
  ];

  const inputCls = 'w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-[14px] text-neutral-900 outline-none focus:border-neutral-900 transition-colors placeholder:text-neutral-400';

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center px-4 py-6"
          style={{ background: 'rgba(12,12,14,0.82)', backdropFilter: 'blur(14px)' }} data-testid="welcome-gate">
          <motion.div initial={{ y: 22, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
            className="relative w-full max-w-xl rounded-3xl bg-white shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: 'min(620px, 92vh)' }}>
            <div className="flex items-center gap-3 px-7 pt-5 pb-4 shrink-0">
              <span className="relative w-2.5 h-2.5 rounded-full bg-emerald-400">
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-40" />
              </span>
              <p className="flex-1 text-[12px] font-semibold tracking-[0.14em] uppercase text-neutral-400">
                Rachel <span className="text-neutral-300 normal-case tracking-normal">· Kitxens AI</span>
              </p>
              <button onClick={skip} aria-label="Close" data-testid="welcome-gate-skip"
                className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-300 hover:text-neutral-800 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7 pb-5 space-y-4" data-testid="welcome-gate-chat">
              <p className="text-[22px] sm:text-[25px] text-neutral-900 leading-snug whitespace-pre-line" style={SERIF} data-testid="welcome-gate-greeting">
                {typed}{typed.length < greeting.length && <span className="inline-block w-[2px] h-[1em] bg-neutral-900 align-middle ml-0.5 animate-pulse" />}
              </p>

              {msgs.map((m, i) => (
                <motion.div key={i} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                  className={m.from === 'user'
                    ? 'ml-auto max-w-[85%] rounded-2xl rounded-tr-md bg-neutral-900 px-4 py-2.5 text-[14px] text-white leading-relaxed'
                    : 'max-w-[94%] text-[15px] text-neutral-700 leading-relaxed'}>
                  {m.text}
                </motion.div>
              ))}

              {thinking && (
                <div className="flex items-center gap-1.5 py-1" data-testid="welcome-gate-thinking">
                  {[0, 1, 2].map((d) => (
                    <span key={d} className="w-1.5 h-1.5 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                  ))}
                </div>
              )}

              {step === 'contact' && !thinking && (
                <motion.div initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="space-y-2 pt-1">
                  <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    placeholder={isEs ? 'Nombre y apellidos' : 'First & last name'} data-testid="welcome-gate-name" className={inputCls} />
                  <input value={contact.restaurant} onChange={(e) => setContact({ ...contact, restaurant: e.target.value })}
                    placeholder={isEs ? 'Tu restaurante' : 'Your restaurant'} data-testid="welcome-gate-restaurant" className={inputCls} />
                  <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitContact(false); }}
                    placeholder="Email" type="email" data-testid="welcome-gate-email" className={inputCls} />
                  <div className="flex items-center gap-4 pt-1">
                    <button onClick={() => submitContact(false)} disabled={!/.+@.+\..+/.test(contact.email)} data-testid="welcome-gate-contact-submit"
                      className="rounded-full bg-neutral-900 px-7 py-2.5 text-[13px] font-semibold text-white disabled:opacity-30 hover:bg-neutral-700 transition-colors">
                      {isEs ? 'Continuar' : 'Continue'}
                    </button>
                    <button onClick={() => submitContact(true)} data-testid="welcome-gate-contact-skip"
                      className="text-[12.5px] text-neutral-400 hover:text-neutral-700 transition-colors">
                      {isEs ? 'Prefiero mirar con calma' : 'I\u2019d rather browse calmly'}
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 'route' && !thinking && (
                <motion.div initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="space-y-2 pt-1">
                  {routes.map((r) => (
                    <button key={r.id} onClick={() => go(r.to, r.id)} data-testid={`welcome-gate-route-${r.id}`}
                      className={r.primary
                        ? 'w-full text-left rounded-2xl bg-neutral-900 px-5 py-3.5 flex items-center gap-3 text-[14px] font-semibold text-white hover:bg-neutral-700 transition-colors group'
                        : 'w-full text-left rounded-2xl border border-neutral-200 px-5 py-3 flex items-center gap-3 text-[13.5px] text-neutral-700 hover:border-neutral-900 transition-colors group'}>
                      <span className="flex-1">{r.label}</span>
                      <ArrowRight className={`w-4 h-4 shrink-0 ${r.primary ? 'text-[#F77F11]' : 'text-neutral-300 group-hover:text-neutral-900'} transition-colors`} />
                    </button>
                  ))}
                </motion.div>
              )}
              <div ref={bottomRef} />
            </div>

            {step === 'pain' && (
              <div className="px-7 pb-6 pt-1 shrink-0">
                <div className="flex items-end gap-2 rounded-2xl border border-neutral-200 bg-white px-4 py-3 focus-within:border-neutral-900 transition-colors">
                  <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} data-testid="welcome-gate-input"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendPain(input); } }}
                    placeholder={ph || ' '}
                    className="flex-1 resize-none bg-transparent text-[14.5px] text-neutral-900 outline-none placeholder:text-neutral-400 leading-relaxed max-h-24"
                    style={{ minHeight: 24 }} />
                  <button onClick={() => sendPain(input)} disabled={!input.trim()} aria-label="Send" data-testid="welcome-gate-send"
                    className="w-9 h-9 rounded-full bg-neutral-900 flex items-center justify-center text-white disabled:opacity-20 hover:bg-neutral-700 transition-colors shrink-0">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-3">
                  {CHIPS.map(([id, es, en]) => (
                    <button key={id} onClick={() => sendPain(isEs ? es : en, id)} data-testid={`welcome-gate-chip-${id}`}
                      className="rounded-full border border-neutral-200 px-3.5 py-1.5 text-[12px] text-neutral-500 hover:border-neutral-900 hover:text-neutral-900 transition-colors">
                      {isEs ? es : en}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeGate;
